import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function Home({ isLoggedIn, user }) {
    const [events, setEvents] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchEvents = async () => {
          try {
            console.log("Fetching events from server...");
            const [wpRes, ongoingRes] = await Promise.all([
              fetch("https://414ministry.org/upcomingevents.json"),
              fetch("/ongoingevents.json")
            ]);
      
            if (!wpRes.ok || !ongoingRes.ok) {
              throw new Error("Failed to load events");
            }
      
            const wpEvents = await wpRes.json();
            const ongoingEvents = await ongoingRes.json();
      
            // Merge them: keep WP format but append ongoing ones
            const combined = [
              ...wpEvents,
              ...ongoingEvents.map(ev => ({
                ...ev,
                type: "ongoing"
              }))
            ];
      
            console.log("Fetched combined events:", combined);
            setEvents(combined);
          } catch (err) {
            console.error("Error fetching events:", err);
            setError("Failed to load events: " + err.message);
          } finally {
            setLoading(false);
          }
        };
        fetchEvents();
      }, []);  

    const parseEventDetails = (event) => {
        let date = 'Date TBD';
        if (event.meta?.event_date) {
            const year = event.meta.event_date.substring(0, 4);
            const month = event.meta.event_date.substring(4, 6);
            const day = event.meta.event_date.substring(6, 8);
            date = `${month}/${day}/${year}`;
        }
        return { date };
    };

    const cleanExcerpt = (excerpt) => {
        if (!excerpt) return 'No description available.';
        const cleanText = excerpt.replace(/<[^>]+>/g, '').trim();
        return cleanText.length > 150 ? cleanText.substring(0, 147) + '...' : cleanText;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-600">Loading events...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="relative bg-gray-900 overflow-hidden" style={{ minHeight: isLoggedIn ? '15rem' : '25rem' }}>
                <div className="absolute inset-0">
                    <img
                        className="w-full h-full object-cover opacity-30"
                        src="/hero-bg.jpg"
                        alt="414 Ministry Background"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-gray-900 mix-blend-multiply"></div>
                </div>
                <div className="relative max-w-7xl mx-auto py-8 px-4 sm:py-10 sm:px-6 lg:px-8 flex items-center justify-center h-full">
                    <div className="text-center">
                        <img src="/414_logo.png" alt="414 Ministry Logo" className={isLoggedIn ? "mx-auto w-20 h-20 object-contain mb-4" : "mx-auto w-32 h-32 object-contain mb-8"} />
                        <h1 className={isLoggedIn ? "text-2xl font-extrabold tracking-tight text-white sm:text-3xl" : "text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl"}>
                            Welcome to 414 Ministry
                        </h1>
                        <p className={isLoggedIn ? "mt-2 text-base text-gray-300 max-w-xl mx-auto" : "mt-6 text-xl text-gray-300 max-w-3xl mx-auto"}>
                            {isLoggedIn ? "You're part of our community—grow with us!" : "Empowering men to live out authentic manhood through faith, fellowship, and community. Join us as we grow together in Christ."}
                        </p>
                        {!isLoggedIn && (
                            <div className="mt-10 flex justify-center gap-4">
                                <button
                                    onClick={() => navigate('/register')}
                                    className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10 transition-colors duration-300"
                                >
                                    Join Us
                                </button>
                                <button
                                    onClick={() => window.location.href = 'mailto:ntmoore52@gmail.com'}
                                    className="px-8 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-gray-900 md:py-4 md:text-lg md:px-10 transition-colors duration-300"
                                >
                                    Contact Us
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <section className="mb-16">
                    <div className="text-center mb-8">
                        <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl">Our Events</h2>
                        <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
                            Stay updated with our latest gatherings to grow in faith with fellow men.
                        </p>
                    </div>
                    
                    {error ? (
                        <div className="text-center">
                            <p className="text-red-600 bg-red-50 p-4 rounded-lg inline-block">{error}</p>
                        </div>
                    ) : events.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {events.map(event => {
                                if (!event || !event.content || !event.content.rendered) {
                                    return null;
                                }
                                const { date } = parseEventDetails(event);
                                return (
                                    <div key={event.id} className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                                        <div className="p-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    {date}
                                                </div>

                                                {event.badge && (
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-600 text-white">
                                                    {event.badge}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-2xl font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300">
                                                {event.yoast_head_json?.og_title?.replace(' - Calvary Baptist Church', '') || 'Untitled Event'}
                                            </h3>
                                            <p className="text-gray-600 mb-4 line-clamp-3">
                                                {cleanExcerpt(event.content.rendered)}
                                            </p>
                                            <button 
                                                onClick={() => navigate(`/event/${event.id}`)}
                                                className="inline-flex items-center px-5 py-2 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-300"
                                            >
                                                View Details
                                                <svg className="ml-2 -mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-gray-50 rounded-xl">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <h3 className="mt-2 text-lg font-medium text-gray-900">No upcoming events</h3>
                            <p className="mt-1 text-gray-600">Check back soon for new events!</p>
                        </div>
                    )}
                </section>

                <section className="mb-16">
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="px-6 py-10 sm:px-12 lg:px-16">
                            <div className="max-w-3xl mx-auto text-center">
                                <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-6">
                                    About 414 Ministry
                                </h2>
                                <div className="space-y-6 text-lg text-gray-700">
                                    <p className="leading-relaxed">
                                        414 Ministry, part of Calvary Baptist Church in Latonia, KY, is dedicated to discipling men, fostering deep relationships, 
                                        and empowering men to live out the authentic manhood God has called us to embody.
                                    </p>
                                    <p className="leading-relaxed">
                                        We stand together to fight for what truly matters—faith, family, and community—through events like:
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
                                        <div className="bg-blue-50 rounded-xl p-6">
                                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                                <img src="/mcb_favicon.png" alt="Man Cave Bash Icon" className="w-6 h-6" />
                                            </div>
                                            <h3 className="font-semibold text-gray-900 mb-2">Man Cave Bashes</h3>
                                            <p className="text-sm text-gray-600 mb-2">Gather with fellow men for fellowship and fun</p>
                                            <button 
                                                onClick={() => navigate(`/man-cave-bash`)}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-300"
                                            >
                                                Learn more
                                                <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="bg-blue-50 rounded-xl p-6">
                                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                </svg>
                                            </div>
                                            <h3 className="font-semibold text-gray-900 mb-2">Crucible Men</h3>
                                            <p className="text-sm text-gray-600 mb-2">Deep dive into spiritual growth and leadership</p>
                                            <button 
                                                onClick={() => navigate(`/crucible-men`)}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-300"
                                            >
                                                Learn more
                                                <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="bg-blue-50 rounded-xl p-6">
                                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <h3 className="font-semibold text-gray-900 mb-2">Dads & Kids Campout</h3>
                                            <p className="text-sm text-gray-600">Build lasting memories with your children</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mb-16">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-lg overflow-hidden">
                        <div className="px-6 py-10 sm:px-12 lg:px-16">
                            <div className="max-w-3xl mx-auto text-center">
                                <blockquote className="text-white">
                                    <p className="text-xl sm:text-2xl font-medium italic leading-relaxed">
                                        "Do not be afraid of them. Remember the Lord, who is great and awesome, and fight for your brothers, your sons, your daughters, your wives, and your homes."
                                    </p>
                                    <footer className="mt-6 text-blue-100">
                                        <p className="font-semibold">Nehemiah 4:14</p>
                                    </footer>
                                </blockquote>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default Home;